// Применяет HeadModel к живому document.head (клиент). Идемпотентно: на
// первом запуске поверх пререндеренного HTML просто подтверждает те же
// значения, при SPA-переходе, переписывает и убирает лишнее (например,
// robots=noindex с предыдущей страницы). Управляет только тегами с data-seo.

import { SEO_ATTR, serializeJsonLd, type HeadModel } from './head';

export function applyHead(model: HeadModel): void {
  if (typeof document === 'undefined') return;
  const head = document.head;
  const keep = new Set<Element>();

  if (document.title !== model.title) document.title = model.title;

  for (const m of model.metas) {
    const selector = m.name
      ? `meta[name="${m.name}"]`
      : `meta[property="${m.property}"]`;
    let el = head.querySelector<HTMLMetaElement>(selector);
    if (!el) {
      el = document.createElement('meta');
      if (m.name) el.setAttribute('name', m.name);
      else el.setAttribute('property', m.property ?? '');
      el.setAttribute(SEO_ATTR, '');
      head.appendChild(el);
    }
    if (el.getAttribute('content') !== m.content) el.setAttribute('content', m.content);
    keep.add(el);
  }

  for (const l of model.links) {
    let el = head.querySelector<HTMLLinkElement>(`link[rel="${l.rel}"]`);
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', l.rel);
      el.setAttribute(SEO_ATTR, '');
      head.appendChild(el);
    }
    if (el.getAttribute('href') !== l.href) el.setAttribute('href', l.href);
    keep.add(el);
  }

  // Свои теги, которых нет в новой модели (canonical/keywords/robots прошлой страницы).
  head.querySelectorAll(`meta[${SEO_ATTR}], link[${SEO_ATTR}]`).forEach((el) => {
    if (!keep.has(el)) el.remove();
  });

  // JSON-LD проще пересобрать целиком, сущности у страниц разные.
  const scripts = model.jsonLd.map((node) => serializeJsonLd(node));
  const existing = Array.from(
    head.querySelectorAll<HTMLScriptElement>(`script[type="application/ld+json"][${SEO_ATTR}]`),
  );
  const unchanged =
    existing.length === scripts.length && existing.every((el, i) => el.textContent === scripts[i]);
  if (unchanged) return;
  existing.forEach((el) => el.remove());
  for (const json of scripts) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(SEO_ATTR, '');
    script.textContent = json;
    head.appendChild(script);
  }
}
